import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Inject, Injectable, Type } from '@angular/core';
import { doc, docData, DocumentReference, getFirestore, runTransaction, serverTimestamp, Timestamp, updateDoc } from '@angular/fire/firestore';
import { WW_REPAIR_BATCH_JOBS_COLLECTION } from '@damofire/data-commons';
import { Asset, Issue, IssueService, RepairService } from '@damofire/data-watson';
import { SnackService } from '@damofire/uix-commons';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, catchError, combineLatest, map, Observable, of, shareReplay, startWith, Subject } from 'rxjs';
import { AppAuthService } from '../../../security/app-auth.service';
import { type BatchRepairImportData, type ResolvedBatchRow } from './batch-repair-csv.utils';
import { BATCH_REPAIR_PROGRESS_CARD } from './batch-repair-progress-card.token';

interface BatchRepairJobDoc {
  status: 'running' | 'completed' | 'failed';
  divisionId: string;
  organizationId: string;
  initiatedByEmail: string | null;
  locale: string;
  startedAt: Timestamp;
  lastHeartbeatAt?: Timestamp;
  completedAt?: Timestamp;
  current: number;
  total: number;
  repairsCreated: number;
  issuesResolved: number;
  error?: string;
}

export interface BatchRepairProgress {
  warehouseId: string;
  current: number;
  total: number;
  issues: number;
  failed: boolean;
}

const STALE_LOCK_THRESHOLD_MS = 30 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class BatchRepairProgressService {
  private readonly progressSubject = new BehaviorSubject<BatchRepairProgress | null>(null);
  private readonly doneSubject = new Subject<boolean>();
  private readonly runningCache = new Map<string, Observable<boolean>>();
  private overlayRef: OverlayRef | null = null;
  private userEmail: string | null = null;

  readonly progress: Observable<BatchRepairProgress | null> = this.progressSubject.asObservable();
  readonly onDone: Observable<boolean> = this.doneSubject.asObservable();

  constructor(
    private readonly overlay: Overlay,
    private readonly repairService: RepairService,
    private readonly issueService: IssueService,
    private readonly snackService: SnackService,
    private readonly authService: AppAuthService,
    private readonly translate: TranslateService,
    @Inject(BATCH_REPAIR_PROGRESS_CARD) private readonly progressCard: Type<unknown>,
  ) {
    this.loadUserEmail();
  }

  isRunningForWarehouse$(warehouseId: string): Observable<boolean> {
    if (!this.runningCache.has(warehouseId)) {
      const firestoreRunning$ = docData<BatchRepairJobDoc>(
        doc(getFirestore(), WW_REPAIR_BATCH_JOBS_COLLECTION, warehouseId) as DocumentReference<BatchRepairJobDoc>,
      ).pipe(
        map((job) => !!job && job.status === 'running' && !this.isStale(job)),
        catchError(() => of(false)),
        startWith(false),
      );
      const localRunning$ = this.progressSubject.pipe(
        map((p) => p !== null && p.warehouseId === warehouseId && !p.failed && p.current < p.total),
      );
      this.runningCache.set(
        warehouseId,
        combineLatest([firestoreRunning$, localRunning$]).pipe(
          map(([fromFirestore, fromLocal]) => fromFirestore || fromLocal),
          shareReplay({ bufferSize: 1, refCount: true }),
        ),
      );
    }
    return this.runningCache.get(warehouseId)!;
  }

  async startImport(rows: ResolvedBatchRow[], assetsById: Map<string, Asset>, data: BatchRepairImportData): Promise<boolean> {
    if (this.isRunning) {
      return false;
    }

    const db = getFirestore();
    const jobRef = doc(db, WW_REPAIR_BATCH_JOBS_COLLECTION, data.divisionId) as DocumentReference<BatchRepairJobDoc>;
    const groups = groupByUpright(rows);

    const acquired = await runTransaction(db, async (tx) => {
      const existing = await tx.get(jobRef);
      if (existing.exists()) {
        const job = existing.data();
        if (job.status === 'running' && !this.isStale(job)) {
          return false;
        }
      }
      tx.set(jobRef, {
        status: 'running',
        divisionId: data.divisionId,
        organizationId: data.organizationId,
        initiatedByEmail: this.userEmail,
        locale: this.translate.currentLang ?? 'en',
        startedAt: serverTimestamp() as unknown as Timestamp,
        current: 0,
        total: groups.length,
        repairsCreated: 0,
        issuesResolved: 0,
      });
      return true;
    });

    if (!acquired) {
      return false;
    }

    this.progressSubject.next({ warehouseId: data.divisionId, current: 0, total: groups.length, issues: 0, failed: false });
    this.openOverlay();
    void this.runImport(groups, assetsById, data, jobRef);
    return true;
  }

  closeOverlay(): void {
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }

  private loadUserEmail(): void {
    void this.authService.currentUser().then((user) => {
      this.userEmail = user?.email ?? null;
    });
  }

  private get isRunning(): boolean {
    const p = this.progressSubject.value;
    return p !== null && !p.failed && p.current < p.total;
  }

  private isStale(job: BatchRepairJobDoc): boolean {
    const ts = job.lastHeartbeatAt ?? job.startedAt;
    if (!ts) {
      return false;
    }
    const tsMs = ts instanceof Timestamp ? ts.toMillis() : (ts as unknown as { seconds: number }).seconds * 1000;
    return Date.now() - tsMs > STALE_LOCK_THRESHOLD_MS;
  }

  private openOverlay(): void {
    const positionStrategy = this.overlay.position().global().centerHorizontally().bottom('24px');
    this.overlayRef = this.overlay.create({ positionStrategy, hasBackdrop: false });
    this.overlayRef.attach(new ComponentPortal(this.progressCard));
  }

  private async runImport(
    groups: ResolvedBatchRow[][],
    assetsById: Map<string, Asset>,
    data: BatchRepairImportData,
    jobRef: DocumentReference<BatchRepairJobDoc>,
  ): Promise<void> {
    const progress = { repairCount: 0, issueCount: 0 };
    try {
      await this.executeGroups(groups, assetsById, data, jobRef, progress);
      this.closeOverlay();
      this.progressSubject.next(null);
      await updateDoc(jobRef, {
        status: 'completed',
        current: groups.length,
        repairsCreated: progress.repairCount,
        issuesResolved: progress.issueCount,
        completedAt: serverTimestamp() as unknown as Timestamp,
      });
      this.snackService.snackThis('watson.repairs.messages.batch.completed', { repairs: progress.repairCount, issues: progress.issueCount });
      this.doneSubject.next(true);
    } catch (error) {
      this.progressSubject.next({ warehouseId: data.divisionId, current: progress.repairCount, total: groups.length, issues: progress.issueCount, failed: true });
      const details = error as { status?: number; error?: unknown; response?: { status?: number; data?: unknown } };
      console.error('Batch repair import stopped after a write failed', {
        status: details.status || details.response?.status,
        response: details.error || details.response?.data,
        error,
      });
      await updateDoc(jobRef, {
        status: 'failed',
        current: progress.repairCount,
        repairsCreated: progress.repairCount,
        issuesResolved: progress.issueCount,
        completedAt: serverTimestamp() as unknown as Timestamp,
        error: String(error),
      }).catch(console.error);
      this.doneSubject.next(false);
    }
  }

  private async executeGroups(
    groups: ResolvedBatchRow[][],
    assetsById: Map<string, Asset>,
    data: BatchRepairImportData,
    jobRef: DocumentReference<BatchRepairJobDoc>,
    progress: { repairCount: number; issueCount: number },
  ): Promise<void> {
    for (const rows of groups) {
      const firstRow = rows[0];
      const asset = assetsById.get(`${firstRow.matchedIssue.asset._id}`);
      const issues: Issue[] = [];
      for (const row of rows) {
        issues.push(await this.issueService.get(data.divisionId, row.matchedIssue._id));
      }
      const repair = await this.repairService.upsert({
        upright: {
          _id: `${asset._id}`,
          description: asset.description || `S${asset.system} / U${asset.upright}`,
        },
        issues,
        productInstalled: firstRow.productInstalled,
        description: joinRepairDescriptions(rows),
        division: { _id: data.divisionId },
        organization: { _id: data.organizationId },
      });
      if (!repair?._id) {
        throw new Error('Repair record was not returned by the repair service.');
      }
      progress.repairCount++;
      await this.issueService.applyRepair(data.divisionId, issues, repair._id, data.organizationId);
      progress.issueCount += rows.length;
      this.progressSubject.next({ warehouseId: data.divisionId, current: progress.repairCount, total: groups.length, issues: progress.issueCount, failed: false });
      await updateDoc(jobRef, { lastHeartbeatAt: serverTimestamp() as unknown as Timestamp });
    }
  }
}

function groupByUpright(rows: ResolvedBatchRow[]): ResolvedBatchRow[][] {
  const grouped = new Map<string, ResolvedBatchRow[]>();
  for (const row of rows) {
    const assetId = row.matchedIssue.asset?._id;
    if (!assetId) {
      continue;
    }
    const key = `${assetId}`;
    const existing = grouped.get(key);
    if (existing) {
      existing.push(row);
    } else {
      grouped.set(key, [row]);
    }
  }
  return Array.from(grouped.values());
}

function joinRepairDescriptions(rows: ResolvedBatchRow[]): string {
  return Array.from(new Set(rows.map((row) => row.repairDescription).filter(Boolean))).join('; ');
}
